import React, { useMemo } from 'react'
import styled from 'styled-components'
import ReactMarkdown, { Components } from 'react-markdown'

// Styles
import { defaultTextColor } from '../styles/colors'
import {
  applyTextStyle,
  defaultTextStyle,
  markDownStyles,
} from '../styles/typography'
import { fastTransition } from '../styles/effects'

// Utils
import { normalizeMarkdown } from '../utils/markdown'

// Types
import { AnyColor } from '../common/models/colors'
import { TextStyleType, TextTagType } from '../common/models/typography'

// The blocks that can open right under another block. Each is re-rendered so
// it can carry data-tight; see markDownStyles for what that closes.
const BLOCK_TAGS = [
  'p',
  'ul',
  'ol',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'blockquote',
] as const

// react-markdown hands a custom component the source node plus a few mdast
// extras (`ordered`, `depth`, `level`) that must not reach the DOM.
interface IMarkdownBlockProps
  extends Omit<React.HTMLAttributes<HTMLElement>, 'children'> {
  node: { position?: { start: { line: number } } }
  children?: React.ReactNode
  ordered?: boolean
  depth?: number
  level?: number
}

const tightBlockComponents = (tightLines: Set<number>): Components =>
  Object.fromEntries(
    BLOCK_TAGS.map((tag) => [
      tag,
      ({
        node,
        children,
        ordered,
        depth,
        level,
        ...rest
      }: IMarkdownBlockProps) =>
        React.createElement(
          tag,
          {
            ...rest,
            'data-tight':
              tightLines.has(node.position?.start.line ?? 0) || undefined,
          },
          children
        ),
    ])
  )

interface IProps {
  marginTop?: number
  marginRight?: number
  marginBottom?: number
  marginLeft?: number
  tag?: TextTagType
  color?: AnyColor
  // Deliberately not named `style`: styled-components v5 Object.assign-merges
  // the reserved `style` prop, which shreds a string value into {0:'l',1:'a',…}
  // for every styled(Text) wrapper.
  textStyle?: TextStyleType
  className?: string
  children?: React.ReactNode
  textAlign?: 'left' | 'right' | 'center'
}

export const Text = (props: IProps) => {
  const tag = props.tag || 'p'
  const color = props.color || defaultTextColor
  const textStyle = props.textStyle || defaultTextStyle

  const marginTop = props.marginTop || 0
  const marginRight = props.marginRight || 0
  const marginBottom = props.marginBottom || 0
  const marginLeft = props.marginLeft || 0
  const textAlign = props.textAlign || 'left'

  return (
    <StyledText
      className={props.className}
      as={tag}
      color={color}
      textStyle={textStyle}
      marginTop={marginTop}
      marginRight={marginRight}
      marginBottom={marginBottom}
      marginLeft={marginLeft}
      textAlign={textAlign}
    >
      {props.children}
    </StyledText>
  )
}

export const MarkDownText = (props: IProps) => {
  const tag = 'div' // should always be div for markdown
  const color = props.color || defaultTextColor
  const textStyle = props.textStyle || defaultTextStyle

  const marginTop = props.marginTop || 0
  const marginRight = props.marginRight || 0
  const marginBottom = props.marginBottom || 0
  const marginLeft = props.marginLeft || 0
  const textAlign = props.textAlign || 'left'

  const source = props.children?.toString() || ''
  // Memoised so the tight-block components keep their identity across renders.
  const { markdown, components } = useMemo(() => {
    const normalized = normalizeMarkdown(source)
    return {
      markdown: normalized.markdown,
      components: tightBlockComponents(normalized.tightLines),
    }
  }, [source])

  if (!source) return null
  return (
    <StyledMarkDownText
      className={props.className}
      as={tag}
      color={color}
      textStyle={textStyle}
      marginTop={marginTop}
      marginRight={marginRight}
      marginBottom={marginBottom}
      marginLeft={marginLeft}
      textAlign={textAlign}
    >
      <ReactMarkdown children={markdown} components={components} />
    </StyledMarkDownText>
  )
}

const StyledText = styled.span<{
  textStyle: TextStyleType
  marginTop?: number
  marginRight?: number
  marginBottom?: number
  marginLeft?: number
  textAlign?: string
}>`
  margin-top: ${(props) => props.marginTop}px;
  margin-right: ${(props) => props.marginRight}px;
  margin-bottom: ${(props) => props.marginBottom}px;
  margin-left: ${(props) => props.marginLeft}px;
  color: ${(props) => props.theme.colors[props.color!]};
  ${(props) => applyTextStyle(props.textStyle)}
  text-align: ${(props) => props.textAlign};
  transition: color ${fastTransition};
`

const StyledMarkDownText = styled.div<{
  textStyle: TextStyleType
  marginTop?: number
  marginRight?: number
  marginBottom?: number
  marginLeft?: number
  textAlign?: string
}>`
  ${markDownStyles()};
  margin-top: ${(props) => props.marginTop}px;
  margin-right: ${(props) => props.marginRight}px;
  margin-bottom: ${(props) => props.marginBottom}px;
  margin-left: ${(props) => props.marginLeft}px;
  color: ${(props) => props.theme.colors[props.color!]};
  ${(props) => applyTextStyle(props.textStyle)}
  text-align: ${(props) => props.textAlign}
`

export default Text
